// Assistant (brief section 8.2): context-scoped conversations, sources, and the model in use.
// Personal context = the personal assistant (private). Workspace context = its chief of staff,
// in a private or a shared conversation.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { ArrowUp, ChevronLeft } from 'lucide-react';
import { Answer, RepoLink, refHref } from '../components/Answer';
import { useContextWorkspace } from '../components/Layout';
import { Demo, Empty, ErrorBox, Field, Loading, LockIcon, Modal } from '../components/ui';
import { api, errorText } from '../lib/api';
import { seconds, usd, when } from '../lib/format';
import { useSession } from '../lib/session';
import type { Agent, ChatMessage, Conversation, ConversationSummary, Project, ResolvedRef, Run } from '../lib/types';
import { useApi } from '../lib/useApi';

export function Assistant() {
  const ws = useContextWorkspace();
  const { cid } = useParams();
  const [params, setParams] = useSearchParams();
  const scope = ws ? ws.slug : 'personal';
  const base = ws ? `/w/${ws.slug}/assistant` : '/assistant';
  const list = useApi<ConversationSummary[]>(`/api/conversations?scope=${scope}`);
  const [creating, setCreating] = useState<boolean>(Boolean(params.get('project')));
  const navigate = useNavigate();

  return (
    <div className={`assistant-page${cid ? ' has-thread' : ''}`}>
      <aside className="conv-list" aria-label="Conversations">
        <div className="row between">
          <h2>Conversations</h2>
          <button type="button" className="btn small primary" onClick={() => setCreating(true)}>
            New
          </button>
        </div>
        {list.error && <ErrorBox error={list.error} retry={list.reload} />}
        {!list.data && !list.error && <Loading />}
        {list.data && list.data.length === 0 && <Empty>No conversations yet.</Empty>}
        <ul>
          {list.data?.map((c) => (
            <li key={c.id}>
              <Link to={`${base}/${c.id}`} className={c.id === cid ? 'current' : ''}>
                <span className="conv-title">
                  {c.private && <LockIcon />} {c.title}
                </span>
                <span className="muted small">
                  {c.active_run ? 'answering…' : when(c.last_message_at)}
                  {c.demo && ' · demo'}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </aside>
      <section className="thread-pane">
        {cid ? (
          <Thread key={cid} id={cid} onChanged={list.reload} back={base} />
        ) : (
          <Intro onNew={() => setCreating(true)} hasAny={Boolean(list.data?.length)} />
        )}
      </section>
      {creating && (
        <NewConversation
          scope={scope}
          projectId={params.get('project')}
          onClose={() => {
            setCreating(false);
            if (params.get('project')) setParams({}, { replace: true });
          }}
          onCreated={(c) => {
            setCreating(false);
            void list.reload();
            navigate(`${base}/${c.id}`);
          }}
        />
      )}
    </div>
  );
}

function Intro({ onNew, hasAny }: { onNew: () => void; hasAny: boolean }) {
  const ws = useContextWorkspace();
  return (
    <div className="intro">
      <h1>{ws ? (ws.kind === 'joint' ? 'Joint chief of staff' : `${ws.name} chief of staff`) : 'Your personal assistant'}</h1>
      <p className="muted">
        {ws
          ? ws.kind === 'joint'
            ? 'Coordinates joint projects and decisions. It reads Joint Ventures only; it cannot see either company.'
            : `Organises ${ws.name}'s work and prepares decisions. It reads ${ws.name} only.`
          : 'Private to you. It can read every workspace you belong to; nobody else sees this conversation.'}
      </p>
      {ws && <p className="muted small">When the workspace has experts (Team page), you choose who answers as you start a conversation.</p>}
      <p className="muted small">Each answer is one Claude Code session with read-only access to the records this conversation's audience may see.</p>
      <button type="button" className="btn primary" onClick={onNew}>
        {hasAny ? 'Start a new conversation' : 'Start the first conversation'}
      </button>
    </div>
  );
}

function NewConversation({
  scope,
  projectId,
  onClose,
  onCreated,
}: {
  scope: string;
  projectId: string | null;
  onClose: () => void;
  onCreated: (c: Conversation) => void;
}) {
  const ws = useContextWorkspace();
  const [title, setTitle] = useState('');
  const [isPrivate, setPrivate] = useState(true);
  const [project, setProject] = useState(projectId ?? '');
  const [error, setError] = useState('');
  const projects = useApi<Project[]>(ws ? `/api/w/${ws.slug}/projects` : null);
  // Who answers: the chief of staff (first) or one of the workspace's experts (brief update 2).
  const agents = useApi<Agent[]>(ws ? `/api/w/${ws.slug}/agents` : null);
  const answering = (agents.data ?? []).filter((a) => a.enabled && a.answers_now);
  const [agentId, setAgentId] = useState('');
  const chosen = answering.find((a) => a.id === agentId) ?? answering[0];

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    try {
      const c = await api<Conversation>('/api/conversations', {
        body: {
          scope,
          private: ws ? isPrivate : true,
          title: title || undefined,
          project_id: project || undefined,
          agent_id: ws && chosen ? chosen.id : undefined,
        },
      });
      onCreated(c);
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <Modal title="New conversation" onClose={onClose}>
      <form onSubmit={create}>
        <Field label="Title">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What is it about?" maxLength={200} />
        </Field>
        {ws ? (
          <>
            {answering.length > 1 && (
              <Field label="Who answers" hint={chosen?.responsibilities}>
                <select value={chosen?.id ?? ''} onChange={(e) => setAgentId(e.target.value)} name="agent">
                  {answering.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            <fieldset className="segmented">
              <legend>Who can read it</legend>
              <label className={isPrivate ? 'on' : ''}>
                <input type="radio" checked={isPrivate} onChange={() => setPrivate(true)} />
                <LockIcon /> Only me
              </label>
              <label className={!isPrivate ? 'on' : ''}>
                <input type="radio" checked={!isPrivate} onChange={() => setPrivate(false)} />
                Everyone in {ws.name}
              </label>
            </fieldset>
            <p className="muted small">
              {isPrivate
                ? `The assistant may also read your private drafts in ${ws.name}.`
                : `Shared: the assistant reads only ${ws.name}'s shared records, never anyone's private drafts.`}
            </p>
            <Field label="About a project (optional)">
              <select value={project} onChange={(e) => setProject(e.target.value)}>
                <option value="">No project</option>
                {projects.data?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title}
                    {p.private ? ' (private)' : ''}
                  </option>
                ))}
              </select>
            </Field>
          </>
        ) : (
          <p className="muted small">Private to you. Your personal assistant reads every workspace you belong to.</p>
        )}
        {error && <p className="msg err">{error}</p>}
        <footer className="modal-foot">
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn primary">
            Start
          </button>
        </footer>
      </form>
    </Modal>
  );
}

// A phone has no Ctrl key to speak of, and an installed app has no browser Back button.
const COARSE = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;
const REDUCED_MOTION = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const LIVE_EVERY_MS = 600;
const REF = /\[\[((?:project|knowledge|decision|task|repo):[0-9a-fA-F-]{36})\]\]/g;
// Spans a reveal must never cut through: a citation, **bold**, `code`.
const WHOLE = /\[\[[^\]]*\]\]|\*\*[^*]+\*\*|`[^`]+`/g;

/** The first `n` characters of `text`, moved forward past any span or surrogate pair it would cut. */
function cut(text: string, n: number): string {
  for (const m of text.matchAll(WHOLE)) {
    if (m.index! < n && n < m.index! + m[0].length) n = m.index! + m[0].length;
  }
  const code = text.charCodeAt(n - 1);
  if (code >= 0xd800 && code <= 0xdbff) n += 1; // never leave half an emoji (M-008)
  return text.slice(0, n);
}

/** A draft as it can be shown: an unclosed ** or ` waits until its partner arrives. */
function tidy(text: string): string {
  for (const mark of ['**', '`']) {
    if (text.split(mark).length % 2 === 0) text = text.slice(0, text.lastIndexOf(mark));
  }
  return text;
}

/** Reveals `target` smoothly between polls (faster when far behind); jumps when it restarts. */
function useReveal(target: string): string {
  const [shown, setShown] = useState(target);
  const current = useRef(target);
  useEffect(() => {
    if (REDUCED_MOTION || !target.startsWith(current.current)) {
      current.current = target;
      setShown(target);
      return;
    }
    let frame = 0;
    const step = () => {
      const have = current.current.length;
      if (have >= target.length) return;
      current.current = cut(target, have + Math.max(1, Math.ceil((target.length - have) / 12)));
      setShown(current.current);
      frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [target]);
  return shown;
}

function Thread({ id, onChanged, back }: { id: string; onChanged: () => void; back: string }) {
  const conv = useApi<Conversation>(`/api/conversations/${id}`);
  const [refs, setRefs] = useState<Map<string, ResolvedRef>>(new Map());
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const bottom = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const c = conv.data;
  const active = c?.active_run_id ?? null;

  // While an answer is being written, ask for it about every 0.6 s (decision 19); once it is
  // done, reload the conversation once for the saved answer and its run details.
  const reload = conv.reload;
  const [live, setLive] = useState('');
  const startLive = useRef<string | null>(null);
  startLive.current = c?.live_text ?? null;
  useEffect(() => {
    setLive(startLive.current ?? '');
    if (!active) return;
    let stopped = false;
    let timer = 0;
    const tick = async () => {
      let wait = LIVE_EVERY_MS;
      try {
        const r = await api<{ active: boolean; text: string | null }>(`/api/runs/${active}/live`);
        if (stopped) return;
        if (!r.active) {
          await reload();
          return;
        }
        setLive(r.text ?? '');
      } catch {
        wait = 2000; // a missed answer on a phone network: try again a little later
      }
      if (!stopped) timer = window.setTimeout(tick, wait);
    };
    timer = window.setTimeout(tick, LIVE_EVERY_MS);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [active, reload]);
  const shown = useReveal(tidy(live));

  // The list on the left shows "answering…" until the run ends: refresh it then.
  const wasActive = useRef(false);
  useEffect(() => {
    if (wasActive.current && !active) onChanged();
    wasActive.current = Boolean(active);
  }, [active, onChanged]);

  // Resolve citations to links (read under the reader's own permissions), the live answer's too.
  const asked = useRef(new Set<string>());
  const wanted = useMemo(() => {
    const s = new Set<string>();
    c?.messages.forEach((m) => m.citations.forEach((x) => s.add(x.ref)));
    for (const m of live.matchAll(REF)) s.add(m[1].toLowerCase());
    return [...s].filter((r) => !refs.has(r) && !asked.current.has(r));
  }, [c, refs, live]);
  useEffect(() => {
    if (!wanted.length) return;
    wanted.forEach((r) => asked.current.add(r));
    api<ResolvedRef[]>(`/api/refs?refs=${encodeURIComponent(wanted.join(','))}`)
      .then((rows) => setRefs((prev) => new Map([...prev, ...rows.map((r) => [r.ref, r] as const)])))
      .catch(() => wanted.forEach((r) => asked.current.delete(r)));
  }, [wanted]);

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [c?.messages.length]);

  // Follow the answer as it grows, unless the reader has scrolled up to read something else.
  useEffect(() => {
    if (!shown) return;
    const root = document.documentElement;
    if (window.innerHeight + window.scrollY >= root.scrollHeight - 160) bottom.current?.scrollIntoView({ block: 'end' });
  }, [shown]);

  async function send(e?: React.FormEvent) {
    e?.preventDefault();
    if (!draft.trim() || sending || active) return;
    setSending(true);
    setError('');
    try {
      await api(`/api/conversations/${id}/messages`, { body: { content: draft } });
      setDraft('');
      if (box.current) box.current.style.height = '';
      await conv.reload();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSending(false);
    }
  }

  async function cancel() {
    if (!active) return;
    try {
      await api(`/api/runs/${active}/cancel`, { method: 'POST' });
    } catch (err) {
      setError(errorText(err));
    }
    await conv.reload();
  }

  if (conv.error) return <ErrorBox error={conv.error} retry={conv.reload} />;
  if (!c) return <Loading what="Loading the conversation" />;
  const blocked = !c.ai_enabled ? 'AI is switched off for this workspace (Settings): nothing is sent to Claude.' : c.ai_paused ? 'AI work is paused in this workspace (kill switch in Settings).' : null;

  return (
    <div className="thread">
      <header className="thread-head">
        <div>
          <Link to={back} className="back-link phone-only">
            <ChevronLeft size={20} strokeWidth={2} aria-hidden="true" />
            Conversations
          </Link>
          <h1>{c.title}</h1>
          <div className="row gap wrap small">
            <span className={`badge ${c.private ? 'private' : 'shared'}`}>
              {c.private ? (
                <>
                  <LockIcon /> Only you can read this conversation
                </>
              ) : (
                c.audience + ' can read this conversation'
              )}
            </span>
            <span className="muted">
              {c.agent} reads: <strong>{c.reach}</strong>
            </span>
            <Demo show={c.demo} />
          </div>
        </div>
        <ModelPicker conv={c} onChange={conv.setData} />
      </header>
      {/* aria-busy: screen readers announce the answer once, when it is complete */}
      <ol className="messages" aria-live="polite" aria-busy={Boolean(active)}>
        {c.messages.length === 0 && (
          <li className="msg-empty muted">
            Ask a question. The assistant looks things up in the records it may read, cites them, and says when it cannot find
            something. It cannot change or send anything.
          </li>
        )}
        {c.messages.map((m) => (
          <MessageItem key={m.id} m={m} refs={refs} />
        ))}
        {active && shown && (
          <li className="message assistant live">
            <div className="msg-head row between">
              <span>
                <strong>Assistant</strong> <span className="muted small">writing…</span>
              </span>
              <button type="button" className="btn small ghost" onClick={cancel}>
                Cancel
              </button>
            </div>
            <Answer text={shown} resolved={refs} />
          </li>
        )}
        {active && !shown && (
          <li className="message pending">
            <span className="spinner" aria-hidden="true" /> {c.agent} is answering
            {'model' in c.choice ? ` with ${c.choice.model}` : ''}…
            <button type="button" className="btn small ghost" onClick={cancel}>
              Cancel
            </button>
          </li>
        )}
      </ol>
      <div ref={bottom} />
      <form className="composer" onSubmit={send}>
        {blocked && <p className="msg warn">{blocked}</p>}
        {error && (
          <p className="msg err" role="alert">
            {error}
          </p>
        )}
        <label className="sr-only" htmlFor={`compose-${id}`}>
          Message
        </label>
        <span className="muted small composer-model">
          {'model' in c.choice
            ? `${c.choice.model} · effort ${c.choice.effort} · stops at ${usd(c.choice.budget_usd)} per answer`
            : c.choice.error}
        </span>
        <div className="composer-row">
          <textarea
            id={`compose-${id}`}
            ref={box}
            rows={COARSE ? 1 : 3}
            value={draft}
            placeholder={active ? 'Wait for the answer…' : COARSE ? 'Ask, or paste notes' : 'Ask, or paste notes (Ctrl+Enter to send)'}
            onChange={(e) => {
              setDraft(e.target.value);
              // grow with the text up to the CSS max-height, then scroll inside
              e.target.style.height = '';
              e.target.style.height = `${e.target.scrollHeight + 2}px`;
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) void send();
            }}
            disabled={Boolean(blocked)}
          />
          <button type="submit" className="btn primary send" disabled={!draft.trim() || sending || Boolean(active) || Boolean(blocked)}>
            <ArrowUp size={20} strokeWidth={2.25} aria-hidden="true" />
            <span className="send-label">{sending ? 'Sending…' : 'Send'}</span>
          </button>
        </div>
      </form>
    </div>
  );
}

function MessageItem({ m, refs }: { m: ChatMessage; refs: Map<string, ResolvedRef> }) {
  if (m.role === 'notice') {
    return <li className="message notice">{m.content}</li>;
  }
  if (m.role === 'user') {
    return (
      <li className="message user">
        <div className="msg-head">
          <strong>{m.author ?? 'You'}</strong> <span className="muted small">{when(m.created_at)}</span>
        </div>
        <div className="msg-body">{m.content}</div>
        {m.run && ['failed', 'refused', 'cancelled'].includes(m.run.status) && (
          <p className="muted small">Not answered: {m.run.error ?? m.run.status}</p>
        )}
      </li>
    );
  }
  return (
    <li className="message assistant">
      <div className="msg-head">
        <strong>Assistant</strong> <span className="muted small">{when(m.created_at)}</span>
      </div>
      <Answer text={m.content} resolved={refs} />
      {m.citations.length > 0 && (
        <div className="sources">
          <span className="muted small">Sources:</span>
          {m.citations.map((ci) => {
            const r = refs.get(ci.ref);
            if (r?.available && r.url) return <RepoLink key={ci.ref} r={r} />;
            const href = r ? refHref(r) : null;
            return href ? (
              <Link key={ci.ref} to={href} className="cite">
                {ci.ref.split(':')[0]} · {r?.title ?? ci.title}
              </Link>
            ) : (
              <span key={ci.ref} className="cite gone">
                {ci.ref.split(':')[0]} · {r && !r.available ? 'no longer available' : ci.title}
              </span>
            );
          })}
        </div>
      )}
      {m.run && <RunDetails run={m.run} />}
    </li>
  );
}

function RunDetails({ run }: { run: Run }) {
  return (
    <details className="run-details">
      <summary>
        {run.model ?? run.model_requested} · {usd(run.cost_usd, 4)} · {seconds(run.duration_ms)}
      </summary>
      <dl>
        <dt>Model</dt>
        <dd>
          {run.model ?? '—'} (asked for {run.model_requested}), effort {run.effort}
        </dd>
        <dt>Cost</dt>
        <dd>
          {usd(run.cost_usd, 4)} measured by Claude Code, ceiling {usd(run.budget_usd)}
        </dd>
        <dt>Tokens</dt>
        <dd>
          {run.input_tokens ?? '—'} in · {run.output_tokens ?? '—'} out · {run.num_turns ?? '—'} turns
        </dd>
        <dt>Looked up</dt>
        <dd>
          {run.tool_calls.length === 0
            ? 'nothing'
            : run.tool_calls.map((t, i) => (
                <div key={i} className="small">
                  {t.tool}
                  {t.args && Object.keys(t.args).length > 0 ? ` ${JSON.stringify(t.args)}` : ''} → {t.returned} record
                  {t.returned === 1 ? '' : 's'}
                </div>
              ))}
        </dd>
      </dl>
    </details>
  );
}

function ModelPicker({ conv, onChange }: { conv: Conversation; onChange: (c: Conversation) => void }) {
  const { me } = useSession();
  const [error, setError] = useState('');
  const choice = 'model' in conv.choice ? conv.choice : null;

  async function patch(body: Record<string, unknown>) {
    setError('');
    try {
      onChange(await api<Conversation>(`/api/conversations/${conv.id}`, { method: 'PATCH', body }));
    } catch (e) {
      setError(errorText(e));
    }
  }

  return (
    <div className="model-picker">
      <label>
        <span className="muted small">Model</span>
        <select
          value={conv.model_override ?? ''}
          onChange={(e) => void patch(e.target.value ? { model_override: e.target.value } : { clear_model: true })}
        >
          <option value="">Default{choice && !conv.model_override ? ` (${choice.model}, from ${choice.model_from})` : ''}</option>
          {me.models.map((m) => (
            <option key={m.id} value={m.id} title={m.note}>
              {m.label} · ${m.input_per_mtok}/${m.output_per_mtok} per M tokens
            </option>
          ))}
        </select>
      </label>
      <label>
        <span className="muted small">Effort</span>
        <select
          value={conv.effort_override ?? ''}
          onChange={(e) => void patch(e.target.value ? { effort_override: e.target.value } : { clear_effort: true })}
        >
          <option value="">Default{choice && !conv.effort_override ? ` (${choice.effort})` : ''}</option>
          {['low', 'medium', 'high', 'xhigh', 'max'].map((x) => (
            <option key={x} value={x}>
              {x}
            </option>
          ))}
        </select>
      </label>
      {error && <p className="msg err small">{error}</p>}
    </div>
  );
}
